/**
 * Edit Indicator Dialog Component (Reports v2)
 *
 * Opens a tall right-side panel showing the chart name input and
 * chart_type picker. Provides a "Configure" button that links to the
 * visual report builder route. Save persists name + chart_type via
 * PATCH /reporting/indicators/{id}/.
 *
 * Note: a dedicated Sheet primitive is not yet shipped in this codebase,
 * so we render a wide Dialog with sheet-like styling for the same effect.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { ExternalLink, Loader2 } from 'lucide-react';
import {
  type DashboardIndicator,
} from '../../../lib/api/reporting';
import { REPORT_VIEW_TYPES } from '../constants';

type ChartType = DashboardIndicator['chart_type'];

interface EditIndicatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  indicator: DashboardIndicator | null;
  saving: boolean;
  onSubmit: (payload: Partial<DashboardIndicator>) => void;
  /**
   * Triggered when the user clicks "Configure" — the parent should route
   * to the visual builder URL (e.g. `/reports?view=builder&indicator={id}`).
   */
  onOpenBuilder: (indicator: DashboardIndicator) => void;
}

export function EditIndicatorDialog({
  open,
  onOpenChange,
  indicator,
  saving,
  onSubmit,
  onOpenBuilder,
}: EditIndicatorDialogProps) {
  const [name, setName] = useState('');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [hint, setHint] = useState('');

  useEffect(() => {
    if (!indicator || !open) return;
    setName(indicator.name || '');
    setChartType(indicator.chart_type || 'bar');
    setHint(indicator.hint || '');
  }, [indicator, open]);

  const handleSave = () => {
    if (!indicator || !name.trim()) return;
    onSubmit({
      name: name.trim(),
      chart_type: chartType,
      hint: hint.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Chart</DialogTitle>
          <DialogDescription>
            Update the chart's name and visualization type. Use Configure for the
            full visual report builder (axes, filters, advanced options).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Chart Name *</Label>
            <Input
              placeholder="Enter chart name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Chart Type *</Label>
            <div className="grid grid-cols-3 gap-3">
              {REPORT_VIEW_TYPES.map((type) => {
                const isSelected = chartType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setChartType(type.value as ChartType)}
                    className={`p-3 border rounded-lg text-left transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {type.icon}
                      <span className="font-medium text-sm">{type.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Enter description (optional)"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              rows={2}
            />
          </div>

          {indicator && (
            <div className="rounded-md border border-dashed p-3 flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="font-medium">Need axes, filters or advanced options?</div>
                <div className="text-xs text-muted-foreground">
                  Open the full visual report builder for this chart.
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenBuilder(indicator)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Configure
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!name.trim() || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Chart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
